import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { RootState } from '@next/core-lib/app';
import { Customer } from '@next/core-lib/business-partner-aggregation-api';
import {
  AbstractFormDialogData,
  AbstractFormDialog,
  AbstractDialogResultData,
  DialogService,
  DIALOG_RETURN_TYPES,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SelectedLocation } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { take, skipWhile } from 'rxjs/operators';
import { ViewMode } from 'src/app/shared/models/shared.model';

import { TailBiting, Pen, pigType } from '../../model/tail-biting.model';
import * as actions from '../../store/actions/tail-biting.actions';
import { getSelectedTailBiting } from '../../store/selectors/tail-biting.selectors';

import { TailBitingDetailPenFormComponent } from '../tail-biting-detail-pen-form/tail-biting-detail-pen-form.component';

export interface TailBitingDetailDialogInputData extends AbstractFormDialogData {
  tailBitingId?: number;
  locationId?: number;
  locations: SelectedLocation[];
  customer: Customer;
  viewMode: ViewMode;
}

export interface TailBitingDetailDialogResultData {
  tailBiting: TailBiting;
}

@Component({
  selector: 'app-tail-biting-detail',
  templateUrl: './tail-biting-detail.component.html',
  styleUrls: ['./tail-biting-detail.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgIf,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    MatTabGroup,
    NgClass,
    MatTab,
    MatTabLabel,
    TailBitingDetailPenFormComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class TailBitingDetailComponent extends AbstractFormDialog<TailBitingDetailDialogResultData> {
  formGroup: FormGroup;
  penFormGroups: FormGroup[] = [];
  tabs: number[] = [];

  pageTitle: string = this.localeService.translate('tail-biting.detail.create.title');

  pigTypes: pigType[] = Object.values(pigType);

  penTabsClass: string;
  currency: string;

  isMobile$ = this.breakpointService.ltSm$;

  tailBiting: TailBiting;
  location: SelectedLocation;

  selectedTabIndex = 0;
  pensDeleted = false;
  pensAdded = false;
  canDeletePen = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TailBitingDetailDialogInputData,
    protected mdDialogRef: MatDialogRef<
      TailBitingDetailComponent,
      AbstractDialogResultData<TailBitingDetailDialogResultData>
    >,
    @Inject(BreakpointServiceInjector)
    private readonly breakpointService: BreakpointService,
    private readonly localeService: LocaleService,
    private readonly store: Store<RootState>,
    private formBuilder: FormBuilder,
    private dialog: DialogService,
  ) {
    super();

    this.isMobile$.subscribe((isMobile) => {
      this.penTabsClass = isMobile ? 'mobile' : '';
    });

    this.data.title = this.localeService.translate(`tail-biting.detail.create.title`);

    this.selectedTabIndex = 0;

    this.buildFormGroups(0);

    const id = this.data.tailBitingId;
    if (id && id > 0) {
      this.store.dispatch(actions.setSelectedTailBitingRequest({ id }));
      this.store
        .select(getSelectedTailBiting)
        .pipe(
          skipWhile((c) => !c || c.id !== id),
          take(1),
        )
        .subscribe((tailBiting) => {
          this.tailBiting = tailBiting;

          this.location = this.data.locations.find((l) => l.id === this.tailBiting.locationId);

          this.pageTitle = `${this.localeService.translate('tail-biting.detail.edit.title')} ${
            tailBiting.name
          }`;

          this.buildFormGroups(this.tailBiting.pens.length);
          this.setFormValues();
        });
    } else {
      this.tabs = [0];
      this.buildFormGroups(this.tabs.length);

      this.location = this.data.locations.find((l) => l.id === this.data.locationId);
    }
  }

  private buildFormGroups(length: number): void {
    this.penFormGroups = [];

    this.buildPenFormGroup(length);

    this.formGroup = this.formBuilder.group({
      tailBitingName: [null, [Validators.required]],
      pigType: [null, [Validators.required]],
      tailDocking: [null, [Validators.required]],
      pens: this.penFormGroups,
    });
  }

  private buildPenFormGroup(length: number) {
    for (let i = 0; i < length; i++) {
      this.penFormGroups.push(
        this.formBuilder.group({
          tailBitingAge: [null, [Validators.required]],
          tailLength: [null, [Validators.required]],
          tailPosition: [null],
          tailDamage: [null, [Validators.required]],
          freshness: [null, [Validators.required]],
        }),
      );
    }
  }

  private setFormValues() {
    this.tabs = [];

    this.formGroup.controls.tailBitingName.setValue(this.tailBiting.name);
    this.formGroup.controls.pigType.setValue(this.tailBiting.pigType);
    this.formGroup.controls.tailDocking.setValue(this.tailBiting.tailDocking);

    this.tailBiting.pens.forEach((pen, index) => {
      this.tabs.push(index);
      if (pen.age > 0) {
        this.penFormGroups[index].controls.tailBitingAge.setValue(pen.age);
        this.penFormGroups[index].controls.tailLength.setValue(pen.tailLength);
        this.penFormGroups[index].controls.tailPosition.setValue(pen.tailPosition);
        this.penFormGroups[index].controls.tailDamage.setValue(pen.tailDamage);
        this.penFormGroups[index].controls.freshness.setValue(pen.freshness);
      }
    });

    this.canDeletePen = this.tabs.length > 1;
  }

  getDate() {
    return this.tailBiting?.date ?? new Date();
  }

  save() {
    if (this.validateForm()) {
      this.tailBiting = {
        ...this.tailBiting,
        locationId: this.tailBiting?.locationId ?? this.data.locationId,
        name: <string>this.formGroup.controls.tailBitingName.value,
        pigType: this.formGroup.controls.pigType.value,
        tailDocking: this.formGroup.controls.tailDocking.value,
        pens: this.getPensFromFormGroups(),
      };

      this.confirm({
        tailBiting: this.tailBiting,
      });
    }
  }

  //save and create new pen (NOT new tailbiting)
  alt() {
    if (this.validateForm()) {
      this.tailBiting = {
        ...this.tailBiting,
        locationId: this.tailBiting?.locationId ?? this.data.locationId,
        name: <string>this.formGroup.controls.tailBitingName.value,
        pigType: this.formGroup.controls.pigType.value,
        tailDocking: this.formGroup.controls.tailDocking.value,
        pens: this.getPensFromFormGroups(),
        createdBy: this.data.customer.name,
        createdAt: new Date().toISOString(),
        date: new Date().toISOString(),
      };

      this.store.dispatch(
        actions.upsertTailBitingRequest({
          request: this.tailBiting,
        }),
      );

      this.store
        .select(getSelectedTailBiting)
        .pipe(
          skipWhile((c) => !c),
          take(1),
        )
        .subscribe((tailBiting) => {
          this.tailBiting = tailBiting;
        });

      this.store.dispatch(actions.clearSelectedTailBiting());

      this.tailBiting = {
        ...this.tailBiting,
        pens: this.getPensFromFormGroups(),
      };

      const newPens: Pen[] = [];

      this.tailBiting.pens.forEach((pen) => {
        newPens.push(pen);
      });

      newPens.push({
        age: 0,
        tailLength: undefined,
        tailDamage: undefined,
        freshness: undefined,
      });

      this.tailBiting = {
        ...this.tailBiting,
        pens: newPens,
      };

      this.buildFormGroups(newPens.length);
      this.setFormValues();
      this.selectedTabIndex = newPens.length;
      this.pensAdded = true;
    }
  }

  delete(penindex: number) {
    this.dialog
      .confirmWarning({
        title: this.localeService.translate('tail-biting.detail.delete-pen-confirmation.title'),
        text: this.localeService.translate('tail-biting.detail.delete-pen-confirmation.message'),
        pushHistoryState: false,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          const newPens: Pen[] = [];

          this.tailBiting.pens.forEach((pen) => {
            newPens.push(pen);
          });

          newPens.splice(penindex, 1);

          this.tailBiting = {
            ...this.tailBiting,
            pens: newPens,
          };

          this.buildFormGroups(this.tailBiting.pens.length);
          this.setFormValues();
          this.selectedTabIndex = this.tailBiting.pens.length >= penindex ? penindex : penindex - 1;
          this.pensDeleted = true;
        }
      });
  }

  cancel() {
    if (
      !(
        this.formGroup.pristine &&
        this.penFormGroups.every((f) => f.pristine) &&
        this.penFormGroups.every(() => !this.pensDeleted && !this.pensAdded)
      )
    ) {
      this.dialog
        .pendingChanges({
          title: this.localeService.translate('tail-biting.detail.item-cancellation.title'),
          subTitle: this.localeService.translate('tail-biting.detail.item-cancellation.subtitle'),
          pushHistoryState: false,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.mdDialogRef.close();
          } else {
            this.canceling = false;
            this.mdDialogRef.disableClose = true;
          }
        });
    } else {
      this.mdDialogRef.close();
    }
  }

  private validateForm(): boolean {
    if (!(this.formGroup.valid && this.penFormGroups.every((f) => f.valid))) {
      this.focusOnInvalidTab();
      return false;
    }
    return true;
  }

  private focusOnInvalidTab() {
    const indexOfFailedTab = this.penFormGroups.findIndex((f: FormGroup) => !f.valid);
    if (indexOfFailedTab >= 0) {
      this.validateAllFormFields(this.penFormGroups[indexOfFailedTab]);
      this.selectedTabIndex = indexOfFailedTab;
    }
  }

  private validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  private getPensFromFormGroups(): Pen[] {
    const pens: Pen[] = [];

    this.penFormGroups.forEach((penGroup) => {
      pens.push({
        age: penGroup.controls.tailBitingAge.value,
        tailLength: penGroup.controls.tailLength.value,
        tailPosition: penGroup.controls.tailPosition.value,
        tailDamage: penGroup.controls.tailDamage.value,
        freshness: penGroup.controls.freshness.value,
      });
    });

    return pens;
  }
}
